import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, MapPin, Plus, Search, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { searchFranceTravail } from '../../../services/api.js'
import { refreshCreditsDisplay } from '../../../utils/creditsHelper.js'
import { useAuthStore } from '../../../stores/authStore.js'
import { useWorkerJobsStore } from '../../../stores/workerJobsStore.js'
import { useWorkerJob } from '../../../hooks/useWorkerJob.js'
import {
  LOCATION_OPTIONS,
  SEARCH_CREDIT_COST,
  SEARCH_DEFAULT_PROFILES,
  SEARCH_MAX_PROFILES,
  getFranceTravailAccount,
} from '../constants.js'

const inputClass =
  'w-full rounded-2xl border border-ink/10 bg-ink/[0.03] px-4 py-3 text-sm text-ink outline-none transition placeholder:text-ink/30 focus:border-[#5b5cf4]/40 focus:ring-1 focus:ring-[#5b5cf4]/20'

export function SearchJobStatus({ job }) {
  const progress = job?.progress
  const total = progress?.total || null
  const current = progress?.current ?? 0
  const percent = total ? Math.min(100, Math.round((current / total) * 100)) : null
  const labels = {
    queued: ['En attente', 'Une autre recherche ou demande POEI est en cours : la vôtre démarre juste après.'],
    running: ['En cours', progress?.message || 'Le robot parcourt France Travail…'],
    completed: ['Terminée', `${job?.result?.cv_queued ?? 0} CV en cours d'analyse. Les candidats arrivent dans le CRM au fur et à mesure.`],
    failed: ['Échec', (job?.error || 'Erreur inconnue').split('\n')[0]],
  }
  const [title, detail] = labels[job?.status] || labels.queued
  const failed = job?.status === 'failed'

  return (
    <div className={`rounded-2xl border px-4 py-4 text-sm ${failed ? 'border-red-400/30 bg-red-500/10' : 'border-[#5b5cf4]/20 bg-[#5b5cf4]/5'}`}>
      <p className={`font-semibold ${failed ? 'text-red-300' : 'text-[#5b5cf4]'}`}>Recherche : {title}</p>
      <p className="mt-1 text-ink/60">{detail}</p>
      {job?.status === 'running' && percent !== null && (
        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-ink/10">
          <div className="h-full rounded-full bg-[#5b5cf4] transition-all" style={{ width: `${percent}%` }} />
        </div>
      )}
      {['queued', 'running'].includes(job?.status) && (
        <p className="mt-2 text-xs text-ink/40">Vous pouvez quitter cette page : vous serez prévenu à la fin.</p>
      )}
    </div>
  )
}

/**
 * Formulaire de recherche de candidats France Travail (page Prospection).
 * La recherche part dans la file du worker ; elle est suivie ici et par WorkerJobsWatcher.
 */
export default function FranceTravailSearchPanel() {
  const queryClient = useQueryClient()
  const authUser = useAuthStore((state) => state.user)
  const trackJob = useWorkerJobsStore((state) => state.track)

  const [keywords, setKeywords] = useState([''])
  const [locationType, setLocationType] = useState('france')
  const [customLocation, setCustomLocation] = useState('')
  const [maxProfiles, setMaxProfiles] = useState(SEARCH_DEFAULT_PROFILES)
  const [lastJob, setLastJob] = useState(null)
  const { job: liveJob } = useWorkerJob(lastJob?.id)

  const clampProfiles = (value) => Math.min(SEARCH_MAX_PROFILES, Math.max(1, Number(value) || SEARCH_DEFAULT_PROFILES))

  const searchMutation = useMutation({
    mutationFn: searchFranceTravail,
    onSuccess: (job) => {
      setLastJob(job)
      if (job?.id) trackJob({ id: job.id, kind: job.kind })
      refreshCreditsDisplay(queryClient)
      toast.success('Recherche lancée ! Vous serez prévenu à la fin.', { duration: 6000 })
    },
    onError: (error) => {
      if (error?.response?.status === 402) {
        toast.error(`Crédits insuffisants pour lancer une recherche (${SEARCH_CREDIT_COST} crédits requis)`)
      } else {
        toast.error(error?.response?.data?.detail || error?.response?.data?.message || error?.message || 'Erreur lors de la recherche')
      }
    },
  })

  const handleSubmit = (event) => {
    event.preventDefault()
    const filteredKeywords = keywords.map((k) => k.trim()).filter(Boolean)
    if (filteredKeywords.length === 0) {
      toast.error('Ajoute au moins un mot-clé')
      return
    }
    const location = locationType === 'autre'
      ? customLocation.trim()
      : LOCATION_OPTIONS.find((opt) => opt.value === locationType)?.label || ''

    if (!window.confirm(`Cette recherche coûte ${SEARCH_CREDIT_COST} crédits. Voulez-vous continuer ?`)) return
    setLastJob(null)
    searchMutation.mutate({
      keywords: filteredKeywords,
      location,
      account: getFranceTravailAccount(locationType),
      userId: authUser?.id ?? null,
      maxProfiles: clampProfiles(maxProfiles),
    })
  }

  const updateKeyword = (index, value) => setKeywords((prev) => prev.map((k, i) => (i === index ? value : k)))
  const removeKeyword = (index) => setKeywords((prev) => (prev.length === 1 ? [''] : prev.filter((_, i) => i !== index)))

  return (
    <form onSubmit={handleSubmit} className="space-y-5 rounded-[28px] border border-ink/10 bg-ink/[0.03] p-6">
      <div>
        <h2 className="text-xl font-bold text-ink">Candidats France Travail</h2>
        <p className="mt-1 text-sm text-ink/50">
          Le robot parcourt France Travail, récupère les CV, puis chaque CV est analysé et le candidat ajouté à votre CRM.
        </p>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-ink/70">Mots-clés <span className="text-ink/35">(profils recherchés)</span></label>
          <button
            type="button"
            onClick={() => setKeywords((prev) => [...prev, ''])}
            className="inline-flex items-center gap-1 rounded-full border border-[#5b5cf4]/20 bg-[#5b5cf4]/10 px-3 py-1 text-xs font-semibold text-[#5b5cf4] transition hover:border-[#5b5cf4]/40"
          >
            <Plus className="h-3 w-3" />
            Ajouter
          </button>
        </div>
        {keywords.map((keyword, index) => (
          <div key={index} className="flex items-center gap-2">
            <input
              type="text"
              value={keyword}
              onChange={(event) => updateKeyword(index, event.target.value)}
              placeholder="ex. Développeur, Infirmier..."
              className={`flex-1 ${inputClass}`}
            />
            {keywords.length > 1 && (
              <button
                type="button"
                onClick={() => removeKeyword(index)}
                className="flex h-9 w-9 items-center justify-center rounded-xl border border-ink/10 bg-ink/5 text-ink/40 transition hover:border-red-500/30 hover:text-red-400"
                aria-label="Retirer ce mot-clé"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <label htmlFor="ft-location-type" className="text-sm font-medium text-ink/70">Localisation</label>
          <select id="ft-location-type" value={locationType} onChange={(event) => setLocationType(event.target.value)} className={inputClass}>
            {LOCATION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label htmlFor="ft-max-profiles" className="text-sm font-medium text-ink/70">Nombre de candidats</label>
          <input
            id="ft-max-profiles"
            type="number"
            min={1}
            max={SEARCH_MAX_PROFILES}
            value={maxProfiles}
            onChange={(event) => setMaxProfiles(event.target.value)}
            onBlur={() => setMaxProfiles((v) => clampProfiles(v))}
            className={inputClass}
          />
        </div>
      </div>

      {locationType === 'autre' && (
        <div className="space-y-2">
          <label htmlFor="ft-custom-location" className="text-sm font-medium text-ink/70">Préciser la localisation</label>
          <div className="relative">
            <MapPin className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/30" />
            <input
              id="ft-custom-location"
              type="text"
              value={customLocation}
              onChange={(event) => setCustomLocation(event.target.value)}
              placeholder="ex. Paris, Lyon, Île-de-France..."
              className={`${inputClass} pl-11`}
            />
          </div>
        </div>
      )}

      <p className="text-xs text-ink/40">
        Maximum {SEARCH_MAX_PROFILES} candidats. Seuls les profils avec un CV sont retenus ; la recherche peut en trouver moins.
        Coût : {SEARCH_CREDIT_COST} crédits par recherche.
      </p>

      {lastJob && <SearchJobStatus job={liveJob ?? lastJob} />}

      <div className="flex justify-end border-t border-ink/10 pt-5">
        <button
          type="submit"
          disabled={searchMutation.isPending}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-[#5b5cf4] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#4547cc] disabled:cursor-not-allowed disabled:opacity-70"
        >
          {searchMutation.isPending
            ? <><Loader2 className="h-4 w-4 animate-spin" />Recherche en cours...</>
            : <><Search className="h-4 w-4" />Lancer la recherche</>}
        </button>
      </div>
    </form>
  )
}
